import React from 'react';
import { Link } from 'react-router-dom';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Box from '@mui/material/Box/Box';
import { alpha } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { HackathonPayload } from '../../services/hackathon.service';
import { useAuth } from '../../services/useAuth';
import { TeamFeedback } from '../Voting/TeamFeedback';

/**
 * The announced winners, shown to everyone on the hackathon page once the organizer announces them.
 */
export const ResultsCard = ({ hackathon }: { hackathon: HackathonPayload }) => {
    const { user } = useAuth();
    const myTeam = user ? hackathon.ideas.find(i => !i.archived && i.members.some(m => m.email === user.email)) : undefined;
    const awards = hackathon.winners
        .map(award => ({ ...award, idea: hackathon.ideas.find(i => i._id === award.ideaId) }))
        .filter(award => !!award.idea);

    if (awards.length === 0) {
        return null;
    }

    return (
        // stands out by its lift and its trophy badge (never a stripe along one edge)
        <Card id="results" sx={{ scrollMarginTop: 88, boxShadow: theme => `0 22px 44px -26px ${alpha(theme.palette.text.primary, 0.45)}` }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'secondary.main', backgroundColor: theme => alpha(theme.palette.secondary.main, 0.14)
                        }}>
                            <EmojiEventsIcon />
                        </Box>
                        <Typography variant="h3" component="h2">Results</Typography>
                    </Stack>
                    <Box sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(240px, 1fr))' }
                    }}>
                        {awards.map((award, index) => (
                            <Box key={`${award.category}-${index}`} sx={{ backgroundColor: 'var(--surface-muted)', borderRadius: '12px', p: 2, minWidth: 0 }}>
                                <Typography variant="overline" color="text.secondary">{award.category}</Typography>
                                <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
                                    <Link to={`/h/${hackathon.slug}/idea/${award.ideaId}`}>{award.idea!.title}</Link>
                                </Typography>
                                {
                                    // why this team: everyone asked how the Participants' pick was decided (run031)
                                    award.basis && <Typography variant="caption" color="text.secondary" component="div">
                                        {award.basis === 'judges'
                                            ? (award.tied ? "Tied for the highest judges' total; the organizers broke the tie" : "Highest judges' total")
                                            : award.basis === 'participants'
                                                ? (award.tied ? "Tied for the highest score from participants; the organizers broke the tie" : "Highest score from participants")
                                                : 'Chosen by the organizers'}
                                    </Typography>
                                }
                                {
                                    // an idea can win with nobody on it; then there's simply no names line
                                    award.idea!.members.length > 0 && <Typography variant="body2" color="text.secondary">
                                        {award.idea!.members.map(m => m.username).join(', ')}
                                    </Typography>
                                }
                            </Box>
                        ))}
                    </Box>
                    {
                        // your own team's feedback right here, rather than a link to another page
                        myTeam && <Box sx={{ borderTop: '1px solid var(--border-color)', pt: 2 }}>
                            <Typography variant="overline" color="text.secondary">Your team: {myTeam.title}</Typography>
                            <TeamFeedback hackathonId={hackathon.slug} ideaId={myTeam._id} />
                        </Box>
                    }
                </Stack>
            </CardContent>
        </Card>
    );
}
